import { apiGet } from './client';
import type { OperationResult } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Workflow runs — how one workflow has been running for the selected merchant
// ─────────────────────────────────────────────────────────────────────────────
//
// Pairs with backend WorkflowRunHistoryManager (GET api/AIProcessLibrary/Runs). Counts cover every run in the
// window; step figures, medians and timelines come from the latest `tracedRuns` runs. A step's time runs from the
// end of the steps before it to its own checkpoint (a step writes no "started" line).
// ─────────────────────────────────────────────────────────────────────────────

export type RunStatus = 'completed' | 'failed' | 'running' | 'queued' | 'awaiting-input';
export type StepStatus = 'completed' | 'failed' | 'running' | 'waiting' | 'not-reached' | 'not-recorded' | 'not-in-version';

export interface WorkflowRunLogLine {
  atSeconds: number;
  text: string;
}

export interface WorkflowRunStepSpan {
  stepId: string;
  status: StepStatus;
  startSeconds?: number | null;
  endSeconds?: number | null;
  completions: number;
  error?: string | null;
  /** False for a step the run went through that today's definition no longer has. */
  inCurrentDefinition: boolean;
  lines: WorkflowRunLogLine[];
}

export interface WorkflowRunTrace {
  id: number;
  key: string;
  title?: string | null;
  status: RunStatus;
  createdUtc: string;
  startedUtc: string;
  endedUtc?: string | null;
  queueSeconds?: number | null;
  durationSeconds?: number | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  failure?: string | null;
  ranEarlierVersion: boolean;
  steps: WorkflowRunStepSpan[];
  lines: WorkflowRunLogLine[];
}

export interface WorkflowStepRunStats {
  stepId: string;
  title: string;
  isAgent: boolean;
  ran: number;
  failed: number;
  medianSeconds?: number | null;
}

export interface WorkflowRunHistory {
  name: string;
  days: number;
  sinceUtc: string;
  runs: number;
  finished: number;
  failed: number;
  unfinished: number;
  capped: boolean;
  tracedRuns: number;
  medianRunSeconds?: number | null;
  medianQueueSeconds?: number | null;
  medianTokens?: number | null;
  runsWithTokens: number;
  steps: WorkflowStepRunStats[];
  recent: WorkflowRunTrace[];
}

/** GET /api/AIProcessLibrary/Runs?name=&days= — null when no workflow has the name. */
export async function getWorkflowRuns(name: string, days = 30): Promise<WorkflowRunHistory | null> {
  const wrapped = await apiGet<OperationResult<WorkflowRunHistory>>('/api/AIProcessLibrary/Runs', { name, days });
  if (!wrapped.isSuccess) throw new Error(wrapped.message || 'Failed to load runs');
  return wrapped.data ?? null;
}
